import { createSignal, For } from 'solid-js';
import { differenceInCalendarDays } from 'date-fns';
import { Box } from '../../layout/box/box';
import { StyledVerticalSpace } from '../../layout/layout.styles';
import { Select } from '../../select/select';
import { RegistrationNumber, Upma as UpmaType } from '../product-types';
import { Upma } from './upma';

type Props = {
    registrationNumbers: RegistrationNumber[];
};

/**
 * Whether a minor use still belongs in the default list.
 *
 * A revoked UPMA does not become useless on the day it ends. Product already
 * bought may be used through the wind-down period KI sets, and Gullviks decided
 * on 2026-09-29 that we keep showing it for exactly that long, with both dates
 * on the card. Comet Pro is the case that prompted it: its two UPMAs ended
 * 2026-09-15 but may be used until 2026-12-15.
 *
 * A UPMA that ended with no last-use date from KI gets no such grace.
 */
const isStillUsable = ({ approvalCeasedDate, usageForbiddenDate }: UpmaType) => {
    if (!approvalCeasedDate) {
        return true;
    }

    if (!usageForbiddenDate) {
        return false;
    }

    const lastDay = new Date(usageForbiddenDate);

    if (Number.isNaN(lastDay.getTime())) {
        return false;
    }

    return differenceInCalendarDays(lastDay, Date.now()) >= 0;
};

export const Upmas = (props: Props) => {
    const [show, setShow] = createSignal('active');
    const upmas = () => {
        const filteredUpmas: UpmaType[] = [];

        props.registrationNumbers.forEach(({ upmas, isApproved, registrationNumber }) => {
            upmas.forEach((upma) => {
                filteredUpmas.push({
                    ...upma,
                    registrationNumber: {
                        isApproved,
                        registrationNumber,
                        product: {
                            slug: '',
                            name: '',
                        },
                    },
                });
            });
        });

        return filteredUpmas
            .filter((upma) => {
                if (show() === 'all') {
                    return true;
                }

                return isStillUsable(upma);
            })
            .sort((a, b) => new Date(b.approvedDate || '').getTime() - new Date(a.approvedDate || '').getTime());
    };

    return (
        <>
            <Select
                mode="basic"
                value={show()}
                options={[
                    {
                        value: 'active',
                        text: 'Visa enbart gällande beslut',
                    },
                    {
                        value: 'all',
                        text: 'Visa alla',
                    },
                ]}
                onChange={setShow}
            />
            <StyledVerticalSpace size={1} />
            <For each={upmas()} fallback={<Box>Inga beslut hittades</Box>}>
                {upma => <Upma upma={upma} />}
            </For>
        </>
    );
};
